import { useState } from 'react';
import type { JSX } from 'react';
import type { LphGroup, TenantSettings, WorkPackageLibraryEntry } from '@asas/domain';

import { anmeldeUrlMitRueckkehr, erzeugeEntwurfsspeicher } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import { trpc } from '../trpc';

import {
  alsEingabe,
  aufNeuenStand,
  bergen,
  entwurfAus,
  gruppeEntfernen,
  gruppeHinzufuegen,
  gruppenMeldungen,
  gruppeUmbenennen,
  istGeaendert,
  phaseUmschalten,
  pruefeGemerktenEntwurf,
  sicherung,
  vorschau,
} from './lph-gruppierung';
import type {
  GemerkterEntwurf,
  GruppenEntwurf,
  GruppenMeldungen,
  Sicherung,
} from './lph-gruppierung';
import { LphGruppierungAnsicht } from './LphGruppierungAnsicht';

export interface LphGruppierungBereichProps {
  /** Die Kontokennung der angemeldeten Person — Inhaber eines Entwurfs (AC-016). */
  readonly inhaber: string;
  /**
   * Mit `work_package_library:read` (`darfLphVorschau`)? Ohne das Recht wird
   * `workPackageLibrary.list` nie aufgerufen — kein 403, keine Audit-Zeile (AK 4, E7).
   */
  readonly darfVorschau: boolean;
}

const KEINE_MELDUNGEN: GruppenMeldungen = { jeGruppe: new Map(), allgemein: [] };
const ENTWURFS_SCHLUESSEL = entitaetsSchluessel('lph-gruppierung', 'mandant');

/** Wurde der Entwurf vor der Neuanmeldung gesichert? Die Person soll es wissen. */
type Anmeldung = 'keine' | 'mitEntwurf' | 'ohneEntwurf';

/**
 * Die LPH-Gruppierung des eigenen Mandanten pflegen (AC-305).
 *
 * Gezeigt wird der Bereich nur mit `tenant:configure` (App.tsx, E12).
 * Geladen wird erst auf einen bewussten Klick (apps/web/CLAUDE.md, Ansichten).
 * Gespeichert wird die Gruppierung als Ganzes über `tenant.updateSettings` mit
 * `expectedVersion` (E4); nach Erfolg gilt die SERVERANTWORT. Geprüft wird am
 * Server, die Befunde stehen am Feld (E3). Die Vorschau rechnet im Browser
 * gegen die aktiven Bibliothekseinträge und schreibt nichts (E7).
 */
export function LphGruppierungBereich({
  inhaber,
  darfVorschau,
}: LphGruppierungBereichProps): JSX.Element {
  const [geoeffnet, setGeoeffnet] = useState(false);
  const [gespeichert, setGespeichert] = useState<TenantSettings | null>(null);
  const [entwurf, setEntwurf] = useState<GruppenEntwurf[] | null>(null);
  const [hinweis, setHinweis] = useState<readonly LphGroup[] | null>(null);
  const [eintraege, setEintraege] = useState<readonly WorkPackageLibraryEntry[]>([]);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [ladefehler, setLadefehler] = useState<FehlerBefund | null>(null);
  const [fehler, setFehler] = useState<FehlerBefund | null>(null);
  const [meldungen, setMeldungen] = useState<GruppenMeldungen>(KEINE_MELDUNGEN);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [zugriffVerweigert, setZugriffVerweigert] = useState(false);
  const [anmeldung, setAnmeldung] = useState<Anmeldung>('keine');

  const entwuerfe = erzeugeEntwurfsspeicher({ inhaber });
  const anmeldeUrl = anmeldeUrlMitRueckkehr(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );

  /**
   * Merkt Entwurf UND Ausgangsstand vor einer Neuanmeldung (B1, B2 aus dem
   * KI-Agent-Review). Ohne eigene Eingaben entsteht kein Entwurf.
   */
  /** Wendet `sicherung` an (B1, H5, H6, N1) und sagt der Person, ob gesichert wurde. */
  function sichern(was: Sicherung): void {
    if (was.art === 'merken') {
      setAnmeldung(
        entwuerfe.merken(ENTWURFS_SCHLUESSEL, was.gemerkt) ? 'mitEntwurf' : 'ohneEntwurf',
      );
      return;
    }

    if (was.art === 'vergessen') entwuerfe.vergessen(ENTWURFS_SCHLUESSEL);
    setAnmeldung('ohneEntwurf');
  }

  function aktuelleSicherung(): Sicherung {
    return sicherung(gespeichert?.lphGroups ?? null, entwurf);
  }

  async function ladeVorschau(): Promise<void> {
    setVorschauFehler(null);

    try {
      setEintraege(await trpc.workPackageLibrary.list.query());
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: false });

      if (befund.art !== 'abgebrochen') setVorschauFehler(befund.meldung);
    }
  }

  async function laden(): Promise<void> {
    setLaedt(true);
    setLadefehler(null);
    setFehler(null);
    setMeldungen(KEINE_MELDUNGEN);
    setAnmeldung('keine');

    if (darfVorschau) void ladeVorschau();

    try {
      const neu = await trpc.tenant.settings.query();

      if (gespeichert === null || entwurf === null) {
        // Erstes Laden: ein Entwurf von vor der Neuanmeldung wird angeboten,
        // nie abgeschickt (apps/web/CLAUDE.md, Entwürfe).
        const gemerkt = entwuerfe.holen(ENTWURFS_SCHLUESSEL, pruefeGemerktenEntwurf);

        if (gemerkt === null) {
          setEntwurf(entwurfAus(neu.lphGroups));
          setHinweis(null);
        } else {
          const geborgen = bergen(gemerkt.eingabe, neu.lphGroups);

          setEntwurf(geborgen.entwurf);
          setHinweis(geborgen.hinweis);
          setMeldung(geborgen.meldung);
        }
      } else {
        const stand = aufNeuenStand(entwurf, gespeichert.lphGroups, neu.lphGroups);

        setEntwurf(stand.entwurf);
        setHinweis(stand.hinweis);
      }

      setGespeichert(neu);
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: false });

      if (befund.art === 'abgebrochen') return;
      if (befund.art === 'zugriff') setZugriffVerweigert(true);
      // „Eingaben überleben die Neuanmeldung … beim Speichern wie beim Laden“.
      if (befund.neuAnmelden) sichern(aktuelleSicherung());

      setLadefehler(befund);
    } finally {
      setLaedt(false);
    }
  }

  function fehlerZeigen(error: unknown, gesendet: GemerkterEntwurf): void {
    const befund = klassifiziereFehler(error, { schreibzugriff: true });

    if (befund.art === 'abgebrochen' && !befund.moeglicherweiseAusgefuehrt) return;
    if (befund.neuAnmelden) sichern({ art: 'merken', gemerkt: gesendet });
    if (befund.art === 'zugriff') setZugriffVerweigert(true);

    setMeldungen(gruppenMeldungen(befund.felder));
    setFehler(befund);
  }

  async function speichern(): Promise<void> {
    if (gespeichert === null || entwurf === null || sendet || laedt || zugriffVerweigert) return;

    const gesendet: GemerkterEntwurf = { entwurf, basis: [...gespeichert.lphGroups] };
    const eingabe = { expectedVersion: gespeichert.version, lphGroups: alsEingabe(entwurf) };

    setSendet(true);
    setFehler(null);
    setMeldungen(KEINE_MELDUNGEN);
    setMeldung(null);
    setAnmeldung('keine');

    try {
      const neu = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('tenant_settings', gespeichert.id),
        () => trpc.tenant.updateSettings.mutate(eingabe),
      );

      setGespeichert(neu);
      setEntwurf(entwurfAus(neu.lphGroups));
      setHinweis(null);
      entwuerfe.vergessen(ENTWURFS_SCHLUESSEL);
      setMeldung('Die Gruppierung ist gespeichert.');
    } catch (error) {
      fehlerZeigen(error, gesendet);
    } finally {
      setSendet(false);
    }
  }

  /**
   * Jede Bearbeitung verwirft die Feldmeldungen: sie hängen an der Position
   * einer Gruppe und stünden nach „Entfernen“ an der falschen (B3).
   */
  function bearbeiten(neu: GruppenEntwurf[]): void {
    setEntwurf(neu);
    setMeldung(null);
    setMeldungen(KEINE_MELDUNGEN);
  }

  return (
    <LphGruppierungAnsicht
      geoeffnet={geoeffnet}
      onOeffnen={() => {
        setGeoeffnet(true);
        void laden();
      }}
      entwurf={entwurf}
      geaendert={
        gespeichert !== null && entwurf !== null && istGeaendert(entwurf, gespeichert.lphGroups)
      }
      laedt={laedt}
      sendet={sendet}
      gesperrt={zugriffVerweigert}
      ladefehler={ladefehler?.meldung ?? null}
      fehler={fehler === null ? null : fehlermeldung(fehler)}
      meldung={meldung}
      anmeldeUrl={anmeldung === 'keine' ? null : anmeldeUrl}
      anmeldeHinweis={
        anmeldung === 'mitEntwurf'
          ? 'Ihre Eingaben sind gesichert und erscheinen nach der Anmeldung wieder.'
          : anmeldung === 'ohneEntwurf'
            ? 'Es gibt keine ungespeicherten Eingaben, oder sie konnten nicht gesichert werden.'
            : null
      }
      meldungen={meldungen}
      hinweis={hinweis}
      vorschau={
        darfVorschau && gespeichert !== null && entwurf !== null
          ? vorschau(eintraege, entwurf, gespeichert.lphGroups)
          : null
      }
      vorschauFehler={vorschauFehler}
      onNeuLaden={() => {
        void laden();
      }}
      onUmbenennen={(index, label) => {
        if (entwurf !== null) bearbeiten(gruppeUmbenennen(entwurf, index, label));
      }}
      onPhase={(index, phase) => {
        if (entwurf !== null) bearbeiten(phaseUmschalten(entwurf, index, phase));
      }}
      onHinzufuegen={() => {
        if (entwurf !== null) bearbeiten(gruppeHinzufuegen(entwurf));
      }}
      onEntfernen={(index) => {
        if (entwurf !== null) bearbeiten(gruppeEntfernen(entwurf, index));
      }}
      onVerwerfen={() => {
        if (gespeichert === null) return;
        setEntwurf(entwurfAus(gespeichert.lphGroups));
        setHinweis(null);
        setFehler(null);
        setMeldungen(KEINE_MELDUNGEN);
        setMeldung(null);
        entwuerfe.vergessen(ENTWURFS_SCHLUESSEL);
      }}
      onNeuAnmelden={() => {
        // Eingaben nach dem Fehler gehören mit in den Entwurf (H5).
        sichern(aktuelleSicherung());
      }}
      onSpeichern={() => {
        void speichern();
      }}
    />
  );
}

/** Die Meldung zu einem gescheiterten Speichern — je Art, was jetzt zu tun ist. */
export function fehlermeldung(befund: FehlerBefund): string {
  if (befund.art === 'konflikt') {
    return 'Die Konfiguration wurde inzwischen von jemand anderem gespeichert; nichts wurde geschrieben. Bitte „Neu laden“ — Ihre Eingaben bleiben erhalten.';
  }

  if (befund.art === 'eingabe' && befund.felder.length > 0) {
    return 'Die Gruppierung wurde nicht gespeichert. Bitte die markierten Felder prüfen.';
  }

  if (befund.moeglicherweiseAusgefuehrt) {
    return `${befund.meldung} Die Gruppierung könnte bereits gespeichert sein; „Neu laden“ zeigt den Stand.`;
  }

  return befund.meldung;
}
